import consoleService from "@/_services/console"
import { getListsValues, getListValueLabel } from "@/_services/lists"
import { loc, setLocale } from "@/_services/localization"
import { addNotification, addOops } from "@/_services/notification"
import { addHeadScript, getHostConfig, removeHeadScript } from "@/_services/theming"
import {
  envIsDevelopment,
  envIsProduction,
  envIsStaging,
  envIsTest,
  envIsUat,
  getConfigAtPath,
  getEnvironment,
  getIsAdmin,
  getIsMainAdmin,
  getIsSuperAdmin,
  getTimeZone,
  getUser,
  getUserConfiguration,
  getUserPermissions,
} from "@/_services/userConfiguration"
import { debug, getWindowUuid, httpHeaders, localStorageKeys, scrollToModelField, searchParamToObject } from "@/_services/utils"
import axios from "axios"
import * as utilsCommon from "basikon-common-utils"
import cloneDeep from "lodash.clonedeep"
import * as debounce from "lodash.debounce"
// from the public folder of implementations
const externalLibsBasePaths = {
  MobbSign: "/libs/mobbsign/",
}

const externalLibsOptions = {
  // https://developers.mobbeel.com/docs/mobbsign-web/mobbsign-web-sdk-overview.html
  // This is private npm package that creates its own UI because it needs to gather things like biometrics.
  // Doing it by ourselves would be very difficult and prone to errors.
  MobbSign: {
    src: `${externalLibsBasePaths.MobbSign}mobbsign.js`,
    useDangerousSandbox: true,
  },
}

const state = {
  scriptsCache: {},
}

/**
 * This function writes script tags in the html head block.
 * It means that external libs are immediately instanciated.
 * Make sure to call destroyExternalLibs once finished with them, to avoid leaks elsewhere in the app.
 * External libs must be very carefully evaluated before deciding to add them,
 * and most of them we should refuse to do so.
 */
async function fetchExternalLibs(externalLibs) {
  const { imp } = getHostConfig()
  const _externalLibs = Array.isArray(externalLibs) ? externalLibs : externalLibs.split(",")
  let useDangerousSandbox
  for (let i = 0; i < _externalLibs?.length; i++) {
    const extLibName = _externalLibs[i].trim()
    const extLibOptions = externalLibsOptions[extLibName]
    if (!extLibOptions?.src) continue
    if (extLibOptions.useDangerousSandbox) useDangerousSandbox = true
    addHeadScript({ src: `/imp/${imp}${extLibOptions.src}`, name: extLibName })
  }
  return { useDangerousSandbox }
}

/**
 * Same function as in the back-end but sync instead of async.
 */
function userHasPermission(permissionKey) {
  return getIsAdmin() || getIsMainAdmin() || getIsSuperAdmin() || getUserPermissions()?.[permissionKey]
}

let handleModal
function isModalClosed(state, prevState) {
  if (!state?.layout?.modal || !state?.modal || !prevState?.modal) return false
  return state.layout.modal && state.modal.show === false && prevState.modal.show === true
}

async function onHideModal(event, onHide) {
  // event is only provided when clicking on the close button of the modal
  // clicking in the backdrop does not provides it
  // however most of the time it is not useful
  if (typeof onHide === "function") await onHide({ event })
}

export async function wsGet(url, config) {
  return (await axios.get(url, config)).data
}

export async function wsPost(url, data, config) {
  return (await axios.post(url, data, config)).data
}

export async function wsPut(url, data, config) {
  return (await axios.put(url, data, config)).data
}

export async function wsPatch(url, data, config) {
  return (await axios.patch(url, data, config)).data
}

export async function wsDelete(url, config) {
  return (await axios.delete(url, config)).data
}

const SANDBOX_KEYS = {
  PRIMARY: "primary",
  SECONDARY: "secondary",
}
const codeSandboxes = {
  [SANDBOX_KEYS.PRIMARY]: undefined,
  [SANDBOX_KEYS.SECONDARY]: undefined,
}
const iframeId = "script-sandbox"
let previousUserTimeZone
let cachedForbiddenWords

// These globals can help configurators escape the sandbox and access the parent window and document.
const SANDBOX_HIDDEN_GLOBALS = ["frameElement", "opener", "frames", "parent", "top"]
const SANDBOX_WINDOW_ALIASES = ["window", "self", "globalThis"]

function initCodeSandbox(staticArgs, params) {
  const { useDangerousSandbox, useSecondarySandbox } = params || {}
  const userTimeZone = getTimeZone()
  const codeSandboxKey = useSecondarySandbox ? SANDBOX_KEYS.SECONDARY : SANDBOX_KEYS.PRIMARY
  const codeSandbox = codeSandboxes[codeSandboxKey]
  const _iframeId = `${iframeId}-${codeSandboxKey}`

  if (useDangerousSandbox) {
    if (window.codeSandbox?.log) return
    const fullExternalLibsBasePaths = {}
    const { imp } = getHostConfig()
    const extLibsNames = Object.keys(externalLibsBasePaths)
    extLibsNames.forEach(extLibName => (fullExternalLibsBasePaths[extLibName] = `/imp/${imp}${externalLibsBasePaths[extLibName]}`))
    window.codeSandbox = { externalLibsBasePaths: fullExternalLibsBasePaths }
  } else {
    // For uncertain reasons the codeSandbox can be reset by the browser
    // when going back and forth from a page using it.
    // Best guess is that the browser does some JIT work with the optimizing compiler
    // and sometimes decides to thrash the optimized code having the toolbox (but keeping the codeSandbox bare).
    // All that to say that we need to check the presence of one of the toolbox tool
    // instead of the codeSandbox itself.
    if (codeSandbox) {
      if (codeSandbox.log && previousUserTimeZone === userTimeZone) return
      else previousUserTimeZone = userTimeZone

      // The codeSandbox already exists but has been striped of the toolbox like described above.
      // We delete it before creating a new one.
      const existingIframeElement = document.getElementById(_iframeId)
      if (existingIframeElement) existingIframeElement.parentElement.removeChild(existingIframeElement)
    }

    const rootElement = document.getElementById("root")
    const iframeElement = document.createElement("iframe")
    iframeElement.id = _iframeId
    iframeElement.style.display = "none"
    rootElement.appendChild(iframeElement)
    const sandboxWindow = iframeElement.contentWindow

    const safeParent = { location: sandboxWindow.parent.location }

    // Shadow some globals to prevent scripts from escaping the sandbox.
    // This is a best-effort attempt, as some globals are [LegacyUnforgeable] and can't be shadowed.
    for (const property of SANDBOX_HIDDEN_GLOBALS) {
      try {
        Object.defineProperty(sandboxWindow, property, {
          value: property === "parent" ? safeParent : undefined,
          configurable: false,
        })
      } catch {
        /* best-effort: leave the native property in place if it can't be shadowed */
      }
    }

    // proxy to block access to window.top (and other window aliases)
    // since it cant be block using the Object.defineProperty method above
    // because of the way the browser attach top to window
    const safeSandboxWindow = new Proxy(sandboxWindow, {
      get(target, prop) {
        if (prop === "top") return undefined
        if (SANDBOX_WINDOW_ALIASES.includes(prop)) return safeSandboxWindow
        const value = Reflect.get(target, prop, target)
        return typeof value === "function" ? value.bind(target) : value
      },
      set(target, prop, value) {
        return Reflect.set(target, prop, value, target)
      },
    })
    sandboxWindow.__safeSandboxWindow = safeSandboxWindow

    codeSandboxes[codeSandboxKey] = sandboxWindow
  }

  const useDateNoTime = getConfigAtPath("useDateNoTime")

  const toolbox = {
    windowUuid: getWindowUuid(),
    console,
    log: consoleService.showLocalScriptLog === true ? console.log : () => {},
    logGroup: consoleService.showLocalScriptLog === true ? console.group : () => {},
    logGroupEnd: consoleService.showLocalScriptLog === true ? console.groupEnd : () => {},
    logError: consoleService.showLocalScriptError === true ? console.error : () => {},
    loc,
    setLocale,
    getRootUrl: () => window.location.origin,
    getUrlPathname: () => window.location.pathname,
    getUrlSearch: () => searchParamToObject(window.location.search),
    getUserConfiguration,
    getEnvironment,
    isProduction: () => envIsProduction(), // legacy
    envIsProduction,
    envIsUat,
    envIsStaging,
    envIsDevelopment,
    envIsTest,
    ...utilsCommon,
    ...staticArgs,
    axios,
    wsGet,
    wsPost,
    wsPut,
    wsPatch,
    wsDelete,
    getListsValues,
    getListValueLabel,
    debounce,
    cloneDeep,
    scrollToModelField,
    addNotification,
    handleModal,
    FormData,
    userHasPermission,
    // Help configurators to handle iframe events if they need to do so.
    addEventListener: document.defaultView?.addEventListener,
    removeEventListener: document.defaultView?.removeEventListener,
    checkForbiddenWords: async text => {
      if (!text || typeof text !== "string" || !text.trim()) return []

      if (!cachedForbiddenWords) {
        const forbiddenWorkds = await runScript({ scriptName: utilsCommon.fullStackScripts.forbiddenWords, acceptMissing: true })
        if (Array.isArray(forbiddenWorkds) && forbiddenWorkds.length) {
          const validForbiddenWorkds = forbiddenWorkds.filter(word => word && typeof word === "string").map(word => word.toLowerCase())
          if (validForbiddenWorkds.length) cachedForbiddenWords = validForbiddenWorkds
        }
      }
      if (!cachedForbiddenWords) return []

      const lowerText = text.toLowerCase()
      return cachedForbiddenWords.filter(word => lowerText.includes(word))
    },
    formatDate: (date, locale, option) => utilsCommon.formatDate(date, locale, { timeZone: userTimeZone, ...(option || {}) }),
    formatDateTime: (date, locale, option) => utilsCommon.formatDateTime(date, locale, { timeZone: userTimeZone, ...(option || {}) }),
    formatCurrency: (number, locale, options) =>
      utilsCommon.formatCurrency(number, locale, {
        currencyDisplay: getConfigAtPath("currencyDisplay"),
        ...(options === 0
          ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
          : typeof options === "string"
            ? { currency: options }
            : options || {}),
      }),
    dateLastDayOfMonth: (date, monthsToAdd, _useDateNoTime) => utilsCommon.dateLastDayOfMonth(date, monthsToAdd, _useDateNoTime ?? useDateNoTime),
    dateFirstDayOfMonth: (date, monthsToAdd, _useDateNoTime) => utilsCommon.dateFirstDayOfMonth(date, monthsToAdd, _useDateNoTime ?? useDateNoTime),
    dateAdd: (date, period, _useDateNoTime, timeZone) => utilsCommon.dateAdd(date, period, _useDateNoTime ?? useDateNoTime, timeZone || userTimeZone),
    getNextBusinessDay: args => utilsCommon.getNextBusinessDay({ useDateNoTime, ...args }),
    adjustPaymentDay: args => utilsCommon.adjustPaymentDay({ useDateNoTime, timeZone: userTimeZone, ...args }),
    getFirstPeriodicEndDate: args => utilsCommon.getFirstPeriodicEndDate({ useDateNoTime, ...args }),
    getPeriodicDate: args => utilsCommon.getPeriodicDate({ useDateNoTime, timeZone: userTimeZone, ...args }),
    dateIsLastDayOfMonth: (date, _useDateNoTime) => utilsCommon.dateIsLastDayOfMonth(date, _useDateNoTime ?? useDateNoTime),
    refreshOidcAccessToken: async ({ oidcId = localStorage.getItem(localStorageKeys.OIDC.ID), grantType = "refresh_token" } = {}) => {
      localStorage.setItem(localStorageKeys.OIDC.IS_ATTEMPTING_REFRESH, "true")
      const response = await axios.post(`/api/core/oidc/${oidcId}/access-token`, { grantType })
      localStorage.removeItem(localStorageKeys.OIDC.IS_ATTEMPTING_REFRESH)
      return response
    },
    dateHandler: (dateOrYear, month, day) =>
      useDateNoTime
        ? utilsCommon.dateNoTime(dateOrYear, month, day)
        : !(dateOrYear instanceof Date) && month && day
          ? new Date(dateOrYear, month, day)
          : dateOrYear,
    MMYYYY: (date, separator, timeZone) => utilsCommon.MMYYYY(date, separator, timeZone || userTimeZone),
    HHMMSS: (date, separator, timeZone) => utilsCommon.HHMMSS(date, separator, timeZone || userTimeZone),
    YYYYMM: (date, separator, timeZone) => utilsCommon.YYYYMM(date, separator, timeZone || userTimeZone),
    DDMMYYYY: (date, separator, timeZone) => utilsCommon.DDMMYYYY(date, separator, timeZone || userTimeZone),
    YYYYMMDD: (date, separator, timeZone) => utilsCommon.YYYYMMDD(date, separator, timeZone || userTimeZone),
    YYYYMMDDHHMMSS: (date, dateSeparator, hourSeparator, timeZone) =>
      utilsCommon.YYYYMMDDHHMMSS(date, dateSeparator, hourSeparator, timeZone || userTimeZone),
    YYYYMMDDTHHMMSS: (date, dateSeparator, hourSeparator, hhmmss, timeZone) =>
      utilsCommon.YYYYMMDDTHHMMSS(date, dateSeparator, hourSeparator, hhmmss, timeZone || userTimeZone),
  }

  if (useDangerousSandbox) {
    Object.keys(toolbox).forEach(key => (window.codeSandbox[key] = toolbox[key]))
  } else {
    Object.keys(toolbox).forEach(key => (codeSandboxes[codeSandboxKey][key] = toolbox[key]))
  }
}

function destroyCodeSandbox() {
  for (const codeSandboxKey of [SANDBOX_KEYS.PRIMARY, SANDBOX_KEYS.SECONDARY]) {
    const existingIframeElement = document.getElementById(`${iframeId}-${codeSandboxKey}`)
    if (existingIframeElement) existingIframeElement.parentElement.removeChild(existingIframeElement)
    codeSandboxes[codeSandboxKey] = undefined
  }
  destroyExternalLibs()
}

function destroyExternalLibs() {
  delete window.codeSandbox
  for (const externalLibName in externalLibsOptions) {
    delete window[externalLibName]
    removeHeadScript(externalLibName)
  }
}

async function importScript({ libName, importChain }) {
  const scriptCode = await getScript(libName, true)
  if (!scriptCode) return

  // Scripts located in sub-directories have the sub-directories path in their name.
  // However lib scripts are "namespaced" with their script name, which is this case would contain the illegal js character / (slash).
  // To support importing libs located in sub-directories,
  // we impose here by convention that the libs always retain internally their name without the path.
  // Example : `importScripts("libs/myLib")`
  // In the script of lib "myLib" the variable is named "myLib", not "libs/myLib"
  const internalLibName = libName.substring(libName.lastIndexOf("/") + 1)
  return runCodeInSandbox(scriptCode + `\n \nreturn ${internalLibName}`, null, { scriptName: libName, importChain, isLib: true })
}

/**
 * @param {object} params
 * @param {object} params.scriptName Root script name as stored in the state.
 * @param {object} params.importChain An empty object filled progressively with the list of imports. Used for debugging the import stack and detect loops.
 * @param {string[]} params.libNames List of libraries to import.
 * @returns Imports the libraries provided in arguments by returning their namespace object in an array in the same order as the list of names. If only one library name is provided, its namespace object is returned directly (not within an array).
 */
const importScripts = async params => {
  const { scriptName, importChain, libNames } = params || {}
  const libObjectsPromises = []
  const nbArgs = libNames.length

  for (let i = 0; i < nbArgs; i++) {
    const libName = libNames[i]
    if (importChain[scriptName]) {
      importChain[scriptName][libName] = true
    } else {
      importChain[scriptName] = { [libName]: true }
    }

    // TODO very hard
    // The algorithm to detect a loop is quite complex in the front-end side.
    // The difficulty is increased because the codeSandbox is reused for every calls
    // which means the function importScripts is mutated. The variable scriptName holds
    // the name of the last script that called importScripts.
    // One way to solve the last issue is to destroy and create again the codeSandbox every time
    // but this degrades performance. Object.assign, Object.create, .bind, etc. have been tried and stuff
    // around that have been tried, but to no avail.
    // As we are in the front-end, loop-protection is less necessary so we let go for now.

    // if (importChain[libName]?.[scriptName]) {
    //   if (libName === scriptName) {
    //     throw Error(`A library cannot import itself (${libName})`)
    //   } else {
    //     throw Error(`Import loop detected between libraries ${libName} and ${scriptName}`)
    //   }
    // }

    libObjectsPromises.push(importScript({ libName, importChain }))
  }

  const libObjects = await Promise.all(libObjectsPromises)
  return nbArgs === 1 ? libObjects[0] : libObjects
}

async function runCodeInSandbox(code, dynArgs, params) {
  const { scriptName, importChain = {}, isLib, useDangerousSandbox, useSecondarySandbox } = params || {}

  const targetCodeSandbox = useDangerousSandbox
    ? window.codeSandbox
    : codeSandboxes[useSecondarySandbox ? SANDBOX_KEYS.SECONDARY : SANDBOX_KEYS.PRIMARY] || {}
  const evalTarget = useDangerousSandbox ? window : targetCodeSandbox

  // watch out : function syntax cannot be replaced by an arrow function because we use the arguments built-in variable (named here libNames)
  targetCodeSandbox.importScripts = async function () {
    return importScripts({ scriptName, importChain, libNames: arguments })
  }

  if (scriptName && !isLib) {
    const addScriptHeader = config => ({ ...config, headers: { ...config?.headers, [httpHeaders.scriptName]: scriptName } })
    targetCodeSandbox.wsGet = (url, config) => wsGet(url, addScriptHeader(config))
    targetCodeSandbox.wsPost = (url, data, config) => wsPost(url, data, addScriptHeader(config))
    targetCodeSandbox.wsPut = (url, data, config) => wsPut(url, data, addScriptHeader(config))
    targetCodeSandbox.wsPatch = (url, data, config) => wsPatch(url, data, addScriptHeader(config))
    targetCodeSandbox.wsDelete = (url, config) => wsDelete(url, addScriptHeader(config))
  }

  dynArgs && Object.keys(dynArgs).forEach(key => (targetCodeSandbox[key] = dynArgs[key]))

  // legacy scripts are the one using the execute format or don't declare both execute and res.json
  const isLegacyScript = isLib ? false : code.match(/(\(?{)? *execute *(:|})/g) || (!code.match(/res\.json/g) && !code.match(/res\.render/g))
  // if (isLegacyScript) console.log(`%c Script ${scriptName} uses the legacy format (execute) `, "background: #FFB4B4")

  let wrappedCode = code
  if (!isLegacyScript) {
    wrappedCode = useDangerousSandbox
      ? "(async () => {" + code + "})()"
      : // `this` is bound to a frozen empty object rather than the realm global, so `console.log(this)` no longer dumps
        // the iframe contentWindow, allowing sandbox escaping.
        `(async function (${SANDBOX_WINDOW_ALIASES.join(", ")}, ${SANDBOX_HIDDEN_GLOBALS.filter(it => it !== "parent").join(", ")}) {${code}})` +
        `.call(Object.freeze({}), ${SANDBOX_WINDOW_ALIASES.map(() => "__safeSandboxWindow").join(", ")})`
  }
  return evalTarget.eval?.call(window, wrappedCode)
}

function removeScriptFromCache({ scriptName }) {
  delete state.scriptsCache[scriptName]
}

function reset({ itemsToReset = [] } = {}) {
  if (itemsToReset.length) {
    for (const scriptName of itemsToReset) {
      delete state.scriptsCache[scriptName]
    }
  } else {
    state.scriptsCache = {}
  }
  destroyCodeSandbox()
}

function deregister(name) {
  delete state.scriptsCache[name]
}

async function getScript(scriptName, acceptMissing) {
  // technique to prevent fetching the same script when called several times very quickly
  state.scriptsCache[scriptName] =
    state.scriptsCache[scriptName] ||
    new Promise((resolve, reject) =>
      axios
        .get(`/api/script/scripts/${scriptName}${utilsCommon.toQueryString({ acceptMissing })}`)
        .then(({ data }) => resolve(data.code))
        .catch(error => {
          if (acceptMissing) return resolve()
          addOops(error)
          reject(error)
        }),
    )

  return state.scriptsCache[scriptName]
}

async function runScriptLocally({ scriptName, dynArgs, acceptMissing, silent, useSecondarySandbox }) {
  const scriptCode = await getScript(scriptName, acceptMissing)
  if (!scriptCode) return // Script can be missing

  try {
    return runCode(scriptCode, dynArgs, { scriptName, useSecondarySandbox })
  } catch (error) {
    console.error(error)
    if (!silent) addOops(error)
  }
}

async function runScript({ scriptName, scriptCode, body = {}, silent, acceptMissing }) {
  let result
  const sandbox = {
    req: { body, user: getUser() || {}, query: {} },
    res: { json: param => (result = param) },
  }

  if (scriptName && !scriptCode) scriptCode = await getScript(scriptName, acceptMissing)
  if (!scriptCode && acceptMissing) return

  try {
    await runCode(scriptCode, sandbox, { scriptName })
    return result
  } catch (error) {
    console.error(error)
    if (!silent) addOops(error)
  }
}

async function runCode(code, dynArgs, params) {
  const externalLibs = []
  // to also support the option compile-to ES5
  // we don't enforce the extLib declarations to be at the start of the script
  for (const extLibName in externalLibsOptions) {
    if (code.indexOf(`// extLib: ${extLibName}`) !== -1) externalLibs.push(extLibName)
  }
  const { useDangerousSandbox } = await fetchExternalLibs(externalLibs)

  params.useDangerousSandbox = useDangerousSandbox
  const { useSecondarySandbox } = params

  const user = getUser() || { profiles: [] }
  initCodeSandbox({ user, req: { user } }, { useDangerousSandbox, useSecondarySandbox })
  if (dynArgs?.req) dynArgs.req = { ...dynArgs.req, user }
  return runCodeInSandbox(code, dynArgs, params)
}

export async function executeLayout(parameters) {
  let { prevLayout, layoutScriptName, acceptMissing = true, defaultCards, dynArgs, entityType, entity, ...params } = parameters

  if (!layoutScriptName) {
    addOops("Field 'layoutScriptName' is missing in parameters")
    return
  }

  if (entityType) layoutScriptName = `${layoutScriptName}-${entityType}`

  const layoutScript = await runScriptLocally({ scriptName: layoutScriptName, dynArgs, acceptMissing })

  if (!layoutScript) return defaultCards ? { cards: defaultCards } : undefined

  if (!layoutScript?.execute) {
    addOops("Layout script $ does not have an execute() function", layoutScriptName)
    return
  }

  const pathname = window.location?.pathname
  const prevPathname = prevLayout?.pathname

  // Reset the layout cache if the URL changes, as the new URL likely points to a different entity, invalidating the existing cache.
  if (prevPathname && prevPathname !== pathname) {
    if (debug) console.log(`${layoutScriptName}`, `Layout cache has been reset because the URL has changed (${prevPathname} => ${pathname})`)
    params.cache = {}
  } else {
    params.cache = prevLayout?.cache || {}
  }

  if (debug) console.log(`${layoutScriptName} parameters`, params)

  const layout = await layoutScript.execute(params)
  if (layout) {
    layout.cache = params.cache
    layout.pathname = pathname
    // By default we populate the pageContext ourselves but layouts can override it.
    layout.pageContext ??= { entity }
  }
  return layout
}

export async function executeComputation(parameters) {
  const { scriptName, acceptMissing = true, entityVariableName, ...body } = parameters

  const sandbox = {
    req: {
      body,
      user: getUser() || {},
    },
    res: {
      json: param => {
        body[entityVariableName] = param
      },
    },
  }
  await runScriptLocally({ scriptName, acceptMissing, dynArgs: sandbox })
  return body[entityVariableName]
}

export {
  deregister as deregisterScript,
  destroyExternalLibs,
  getScript,
  isModalClosed,
  onHideModal,
  removeScriptFromCache,
  reset as resetScripts,
  runCode,
  runScript,
  runScriptLocally,
}
